import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment, { type Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'antd';
import {
  Card,
  DatePicker,
  Input,
  Select,
  RichArea,
  ButtonGroup,
  Button,
  BUTTON_TYPES,
  notification,
  NOTIFICATION_TYPES,
  Popup,
} from '_atoms';
import { BreadCrumbs } from '_atoms/BreadCrumbs';
import type { SelectOption } from '_atoms/Select';
import { SELECT_TYPES } from '_atoms/Select/constants';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { FormListDoubleSelect } from '_molecules';
import history from 'helpers/history';
import { ACTIONS, Can, ROLES, UI } from 'permission';
import { getLanguages, getLocations } from 'store/selectors/commonSelectors';
import { getUser } from 'store/selectors/authorizeSelectors';
import {
  getForeignLanguages,
  getJobInfo,
  getListLocations,
  getOpeningById,
  setDataOpening,
  getTechnologyAreas,
} from 'actions';
import { candidateLevels } from 'constants/constants';
import { OPENING_STATUSES } from 'constants/opening';
import type { AppDispatch } from 'store';
import type { Opening, TechnologyAreaOption } from 'store/reducers/jobReducer';
import { ReactComponent as Info } from 'dist/icons/info.svg';
import {
  EMPLOYMENT_TYPE_OPTIONS,
  INITIAL_VALUES,
  LEVEL_OPTIONS,
  PRIORITY_OPTIONS,
  RULES,
  WORK_EXPERIENCE_OPTIONS,
  EXPECTED_DATES,
  UTILIZATION_OPTIONS,
} from './constants';
import {
  formValuesMapper,
  initialValuesMapper,
  teamLeadOptionsMapper,
  validatorL,
  type OpeningFormValues,
} from './utils';
import './style.scss';

const FormButtonGroup = ButtonGroup as React.FC<
  React.ComponentProps<typeof ButtonGroup> & {
    onChange?: () => void;
    disabled?: boolean;
    inputProps?: Record<string, unknown>;
  }
>;

interface CreateOpeningProps {
  isClone?: boolean;
}

interface TechnologyAreaSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface LabelValue {
  value: string;
  label: string;
}

export const CreateOpening: React.FC<CreateOpeningProps> = ({ isClone }) => {
  const { t } = useTranslation();
  const { id, openingId } = useParams<{ id: string; openingId?: string }>();
  const [form] = Form.useForm<OpeningFormValues>();
  const dispatch = useDispatch<AppDispatch>();

  const user = useSelector(getUser);

  const [searchOfficeLocations, setSearchOfficeLocations] = useState('');
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [teamLeadOptions, setTeamLeadOptions] = useState<LabelValue[]>([]);
  const [opening, setOpening] = useState<Partial<Opening>>({});
  const [technologyAreas, setTechnologyAreas] = useState<TechnologyAreaSelectOption[]>([]);
  const locations = useSelector(getLocations).map(location => ({
    value: location.uuid,
    label: location.name,
  }));
  const languages = useSelector(getLanguages).map(value => ({ value, label: value }));

  const title = openingId
    ? isClone
      ? t('translation:create_opening.title_copy_opening')
      : t('translation:create_opening.title_edit_opening')
    : t('translation:create_opening.title_creation');

  const editSH_RM_Status =
    user!.active_role!.name !== ROLES.SUPER_ADMIN
      ? isClone ||
        !opening.status ||
        [OPENING_STATUSES.SH_APPROVAL, OPENING_STATUSES.RM_APPROVAL].includes(
          opening.status as typeof OPENING_STATUSES.SH_APPROVAL
        )
      : true;

  const isOpenWaitingEntry =
    opening.status === OPENING_STATUSES.OPEN || opening.status === OPENING_STATUSES.WAITING_FOR_ENTRY;
  const disablePrev =
    opening &&
    Math.abs(moment(opening.expected, 'YYYY-MM-DD').diff(moment(opening.hrl_approval_date as string), 'days')) <=
      Math.abs(EXPECTED_DATES[opening.level as string]?.diff(moment(), 'days'));

  const minExpectedDate =
    user!.active_role!.name !== ROLES.SUPER_ADMIN && isOpenWaitingEntry
      ? disablePrev
        ? moment(opening.hrl_approval_date as string).add(
            EXPECTED_DATES[opening.level as string].diff(moment(), 'days'),
            'days'
          )
        : moment(opening.expected, 'YYYY-MM-DD')
      : user!.active_role!.name !== ROLES.SUPER_ADMIN &&
        opening &&
        (opening.status === OPENING_STATUSES.LM_APPROVAL || opening.status === OPENING_STATUSES.HRL_APPROVAL)
      ? (moment.min as (a: Moment, b: Moment) => Moment)(
          moment(opening.expected),
          moment(opening.rm_approval_date as string).add(
            EXPECTED_DATES[opening.level as string]!.diff(moment(), 'days'),
            'days'
          )
        ).startOf('day')
      : opening
      ? moment(opening.created as string).startOf('day')
      : moment().startOf('day');

  const getTechnologyAreasList = useCallback(
    (currentAreaId?: string) => {
      getTechnologyAreas(id)
        .then(resp => {
          setTechnologyAreas(
            (resp as TechnologyAreaOption[])
              .filter(area => !area.is_outdated || area.uuid === currentAreaId)
              .map(area => ({
                value: area.uuid,
                label: `${area.name}${area.is_outdated ? ' (Outdated)' : ''}`,
                disabled: area.is_outdated,
              }))
          );
        })
        .catch(() => {});
    },
    [id]
  );

  useEffect(() => {
    dispatch(getJobInfo(id)).then(resp => {
      form.setFields([{ name: 'job_name', value: resp!.name }]);
      setTeamLeadOptions(teamLeadOptionsMapper(resp!));
    });
    if (openingId) {
      setLoading(true);
      getOpeningById(id, openingId)
        .then(data => {
          setOpening(data!);
          return data!;
        })
        .then(initialValuesMapper)
        .then(data => {
          if (isClone)
            form.setFields([
              ...data.filter(d => d.name !== 'expected'),
              {
                name: 'expected',
                value:
                  EXPECTED_DATES[
                    (data.find(d => d.name === 'experience_level')?.value as LabelValue)?.value ||
                      candidateLevels[0].value
                  ],
              },
            ]);
          else form.setFields(data);
          getTechnologyAreasList(
            (data.find(d => d.name === 'technology_area')?.value as LabelValue | undefined)?.value
          );
          setLoading(false);
        });
    } else {
      getTechnologyAreasList();
    }
    dispatch(getForeignLanguages());
    dispatch(getListLocations({ job: id }));
  }, []);

  const onCloseExitConfirmation = () => setOpenConfirmation(false);

  const toOpeningsList = () => history.push(`/jobs/${id}/show/openings`);

  const onGoBack = () => {
    const {
      comment,
      requirements_responsibilities,
      technical_requirements,
      technical_responsibilities,
      additional_requirements,
      position_advantages,
      ...touchedValues
    } = form.getFieldsValue(true, (meta: { touched?: boolean }) => meta.touched);
    if (
      comment !== form.getFieldValue('comment_initial') ||
      requirements_responsibilities !== form.getFieldValue('requirements_responsibilities_initial') ||
      technical_requirements !== form.getFieldValue('technical_requirements_initial') ||
      technical_responsibilities !== form.getFieldValue('technical_responsibilities_initial') ||
      additional_requirements !== form.getFieldValue('additional_requirements_initial') ||
      position_advantages !== form.getFieldValue('position_advantages_initial') ||
      Object.keys(touchedValues).length
    ) {
      setOpenConfirmation(true);
    } else {
      toOpeningsList();
    }
  };

  const onValueChange = (
    { experience_level }: { experience_level?: LabelValue },
    { expected }: { expected?: Moment }
  ) => {
    if (user!.active_role!.name === ROLES.SUPER_ADMIN || !experience_level) return;
    if (expected && EXPECTED_DATES[experience_level.value].isAfter(expected)) {
      form.setFieldValue('expected', EXPECTED_DATES[experience_level.value]);
      notification({
        message: 'Note that the expected date of the opening has been updated automatically',
        type: NOTIFICATION_TYPES.WARNING,
      });
    } else if (!expected) {
      form.setFieldValue('expected', EXPECTED_DATES[experience_level.value]);
    }
  };

  const onFinish = (values: OpeningFormValues) => {
    setLoading(true);
    setDataOpening(id, isClone ? undefined : openingId, formValuesMapper(values) as unknown as Partial<Opening>)
      .then(() => {
        notification({
          type: NOTIFICATION_TYPES.SUCCESS,
          message: `Opening expected on ${values.expected.format('MMM DD, YYYY')} has been ${
            openingId ? (isClone ? 'copied' : 'changed') : 'created'
          }.`,
        });
        toOpeningsList();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const resetField = (field: string) => () => form.setFields([{ name: field, value: '' }]);
  const changeValue = (name: string, value: string) => form.setFieldValue(name, value);

  return (
    <div className="wrapper-content opening-creation__wrapper">
      <BreadCrumbs element={t('translation:create_opening.breadcrumb_jobs')} elementName={title} path="/jobs" />
      <Form
        className="opening-creation__form"
        form={form}
        onFinish={onFinish}
        initialValues={INITIAL_VALUES}
        validateTrigger="onSubmit"
        onValuesChange={onValueChange}
      >
        <div className="opening-creation__header-wrapper">
          <Card className="opening-creation__header">
            <Typography.Title1>{title}</Typography.Title1>
            <div className="submit-buttons">
              <Button disabled={loading} onClick={onGoBack}>
                {t('translation:create_opening.button_cancel')}
              </Button>
              <Button htmlType="submit" loading={loading} type={BUTTON_TYPES.PRIMARY}>
                {openingId
                  ? t('translation:create_opening.button_save_opening')
                  : t('translation:create_opening.button_create_opening')}
              </Button>
            </div>
          </Card>
        </div>
        <Card className="opening-creation">
          <Typography.Title2 className="opening-creation__title" weight={TYPOGRAPHY_WEIGHT.BOLD}>
            {t('translation:create_opening.section_general')}
          </Typography.Title2>
          <Row className="select-block" gutter={[32, 16]}>
            <Col lg={12} md={12} sm={24}>
              <Input label={t('translation:create_opening.label_job_name')} disabled itemProps={{ name: 'job_name' }} />
              <Can I={ACTIONS.UPDATE} a={UI.OPENING_TEAM_LEAD} passThrough>
                {editable => (
                  <Select
                    labelInValue
                    label={t('translation:create_opening.label_team_lead')}
                    placeholder={t('translation:create_opening.ph_team_lead')}
                    options={teamLeadOptions as SelectOption[]}
                    itemProps={{ name: 'team_lead', rules: RULES.REQUIRED }}
                    disabled={!editable}
                  />
                )}
              </Can>
              <Can I={ACTIONS.UPDATE} a={UI.OPENING_PRIORITY} passThrough>
                {editable => (
                  <Select
                    labelInValue
                    label={t('translation:create_opening.label_priority')}
                    options={PRIORITY_OPTIONS}
                    itemProps={{ name: 'priority' }}
                    rules={RULES.PRIORITY}
                    disabled={!editable}
                  />
                )}
              </Can>
              <Can I={ACTIONS.UPDATE} a={UI.OPENING_EXPECTED_DATE} passThrough>
                {editable => (
                  <DatePicker
                    label={t('translation:create_opening.label_expected_date')}
                    itemProps={{
                      name: 'expected',
                      rules: RULES.EXPECTED,
                      extra: t('translation:create_opening.label_expected_date_info'),
                    }}
                    disabled={!editable}
                    minDate={minExpectedDate}
                  />
                )}
              </Can>
              <Can I={ACTIONS.UPDATE} a={UI.OPENING_OFFICE_LOCATIONS} passThrough>
                {editable => (
                  <Select
                    labelInValue
                    label={t('translation:create_opening.label_office_location')}
                    placeholder={t('translation:create_opening.ph_office_location')}
                    mode={SELECT_TYPES.MULTIPLE}
                    options={locations}
                    itemProps={{ name: 'offices', rules: RULES.REQUIRED }}
                    disabled={!editable || !editSH_RM_Status}
                    searchValue={searchOfficeLocations}
                    onSearch={setSearchOfficeLocations}
                  />
                )}
              </Can>
            </Col>
          </Row>
          <Can I={ACTIONS.UPDATE} a={UI.OPENING_FOREIGN_LANGUAGES} passThrough>
            {editable => (
              <FormListDoubleSelect
                listName="foreign_languages"
                buttonText={t('translation:create_opening.button_add_language')}
                firstSelectProps={{
                  name: 'language',
                  label: t('translation:create_opening.label_foreign_language'),
                  placeholder: t('translation:create_opening.ph_foreign_language'),
                  options: languages,
                }}
                secondSelectProps={{
                  name: 'level',
                  placeholder: t('translation:create_opening.ph_language_level'),
                  options: LEVEL_OPTIONS,
                }}
                maxCount={editable ? languages.length : 0}
                firstSelectValidator={validatorL('level', t('translation:create_opening.validation_select_language'))}
                secondSelectValidator={validatorL(
                  'language',
                  t('translation:create_opening.validation_select_language_level')
                )}
              />
            )}
          </Can>
          <Can I={ACTIONS.UPDATE} a={UI.OPENING_EMPLOYMENT_TYPE} passThrough>
            {editable => (
              <FormButtonGroup
                label={t('translation:create_opening.label_employment_type')}
                className="employment-type"
                options={EMPLOYMENT_TYPE_OPTIONS}
                onChange={resetField('employment_custom_value')}
                itemProps={{ name: 'employment' }}
                inputProps={{
                  placeholder: t('translation:create_opening.ph_hours'),
                  maxLength: 2,
                  itemProps: { rules: RULES.EXP_TYPE, name: 'employment_custom_value' },
                  onChange: e => changeValue('employment_custom_value', e.target.value.replace(/\D/g, '')),
                  disabled: !editable,
                }}
                withCustom
                disabled={!editable}
              />
            )}
          </Can>
          <Typography.Title2 className="opening-creation__title" weight={TYPOGRAPHY_WEIGHT.BOLD}>
            {t('translation:create_opening.section_technical_part')}
          </Typography.Title2>
          <Can I={ACTIONS.UPDATE} a={UI.OPENING_WORK_EXPERIENCE} passThrough>
            {editable => (
              <FormButtonGroup
                label={t('translation:create_opening.label_required_work_experience')}
                className="required-work-experience"
                options={WORK_EXPERIENCE_OPTIONS}
                onChange={resetField('work_experience_custom_value')}
                itemProps={{ name: 'work_experience' }}
                inputProps={{
                  placeholder: t('translation:create_opening.ph_years'),
                  maxLength: 5,
                  itemProps: { rules: RULES.WORK_EXP, name: 'work_experience_custom_value' },
                  disabled: !editable,
                }}
                withCustom
                disabled={!editable}
              />
            )}
          </Can>
          <Row className="required-experience-level" gutter={{ lg: 32, md: 32, sm: 16 }}>
            <Col lg={12} md={12} sm={24}>
              <Can I={ACTIONS.UPDATE} a={UI.OPENING_EXPERIENCE_LEVEL} passThrough>
                {editable => (
                  <Select
                    labelInValue
                    label={t('translation:create_opening.label_required_experience_level')}
                    options={candidateLevels as unknown as SelectOption[]}
                    itemProps={{ name: 'experience_level', rules: RULES.EXPERIENCE_LEVEL }}
                    disabled={!editable || !editSH_RM_Status}
                  />
                )}
              </Can>
            </Col>
            <Can I={ACTIONS.UPDATE} a={UI.OPENING_EXPERIENCE_LEVEL} passThrough>
              {editable => (
                <Col span={12}>
                  <Form.Item noStyle shouldUpdate={(prev, curr) => prev.experience_level !== curr.experience_level}>
                    {({ getFieldValue }) =>
                      (getFieldValue as (name?: string) => OpeningFormValues)().experience_level.value === 'Custom' && (
                        <Input
                          label={t('translation:create_opening.label_custom_required_experience_level')}
                          placeholder={t('translation:create_opening.ph_custom_experience_level')}
                          itemProps={{ name: 'experience_level_custom_value', rules: RULES.EXPERIENCE_LEVEL_CUSTOM }}
                          disabled={!editable}
                          maxLength={20}
                        />
                      )
                    }
                  </Form.Item>
                </Col>
              )}
            </Can>
          </Row>
          <Row>
            <Col lg={12} md={12} sm={24}>
              <Can I={ACTIONS.UPDATE} a={UI.OPENING_TECHNICAL_AREA} passThrough>
                {editable => (
                  <Select
                    labelInValue
                    label={t('translation:create_opening.label_technology_area')}
                    description={t('translation:create_opening.desc_technology_area')}
                    placeholder={t('translation:create_opening.ph_technology_area')}
                    options={technologyAreas as SelectOption[]}
                    itemProps={{ name: 'technology_area', rules: RULES.REQUIRED }}
                    disabled={!editable}
                  />
                )}
              </Can>
            </Col>
          </Row>
          <Row>
            <Col lg={12} md={12} sm={24}>
              <Select
                labelInValue
                label={t('translation:create_opening.label_affects_utilization')}
                options={UTILIZATION_OPTIONS as unknown as SelectOption[]}
                itemProps={{ name: 'is_affects_utilization', rules: RULES.REQUIRED }}
              />
            </Col>
          </Row>
          <Can I={ACTIONS.UPDATE} a={UI.OPENING_RESPONSIBILITIES} passThrough>
            {editable => (
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.requirements_responsibilities_initial !== curr.requirements_responsibilities_initial
                }
              >
                {({ getFieldValue, setFieldValue }) =>
                  !loading &&
                  openingId &&
                  !getFieldValue('technical_requirements_initial') &&
                  !getFieldValue('technical_responsibilities_initial') && (
                    <>
                      <div className="field-alert">
                        <Info />
                        {t('translation:create_opening.text_requirements_responsibilities_alert')}
                      </div>
                      <Form.Item className="rich-area-item" name="requirements_responsibilities">
                        <RichArea
                          initialValue={getFieldValue('requirements_responsibilities_initial')}
                          label={t('translation:create_opening.label_requirements_responsibilities')}
                          placeholder={t('translation:create_opening.ph_requirements_responsibilities')}
                          onChange={v => setFieldValue('requirements_responsibilities', v)}
                          disabled={!editable}
                          maxLength={10000}
                          warning
                        />
                      </Form.Item>
                    </>
                  )
                }
              </Form.Item>
            )}
          </Can>
          <Can I={ACTIONS.UPDATE} a={UI.OPENING_RESPONSIBILITIES} passThrough>
            {editable => (
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.technical_requirements_initial !== curr.technical_requirements_initial
                }
              >
                {({ getFieldValue, setFieldValue }) => (
                  <Form.Item className="rich-area-item" name="technical_requirements" rules={RULES.REQUIRED}>
                    <RichArea
                      initialValue={getFieldValue('technical_requirements_initial')}
                      label={t('translation:create_opening.label_requirements')}
                      description={t('translation:create_opening.desc_requirements')}
                      placeholder={t('translation:create_opening.ph_requirements')}
                      onChange={v => setFieldValue('technical_requirements', v)}
                      disabled={!editable}
                      maxLength={10000}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            )}
          </Can>
          <Can I={ACTIONS.UPDATE} a={UI.OPENING_RESPONSIBILITIES} passThrough>
            {editable => (
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.technical_responsibilities_initial !== curr.technical_responsibilities_initial
                }
              >
                {({ getFieldValue, setFieldValue }) => (
                  <Form.Item className="rich-area-item" name="technical_responsibilities" rules={RULES.REQUIRED}>
                    <RichArea
                      initialValue={getFieldValue('technical_responsibilities_initial')}
                      label={t('translation:create_opening.label_responsibilities')}
                      description={t('translation:create_opening.desc_responsibilities')}
                      placeholder={t('translation:create_opening.ph_responsibilities')}
                      onChange={v => setFieldValue('technical_responsibilities', v)}
                      disabled={!editable}
                      maxLength={10000}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            )}
          </Can>
          <Can I={ACTIONS.UPDATE} a={UI.OPENING_RESPONSIBILITIES} passThrough>
            {editable => (
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev.additional_requirements_initial !== curr.additional_requirements_initial
                }
              >
                {({ getFieldValue, setFieldValue }) => (
                  <Form.Item className="rich-area-item" name="additional_requirements">
                    <RichArea
                      initialValue={getFieldValue('additional_requirements_initial')}
                      label={t('translation:create_opening.label_additional_requirements')}
                      description={t('translation:create_opening.desc_additional_requirements')}
                      placeholder={t('translation:create_opening.ph_additional_requirements')}
                      onChange={v => setFieldValue('additional_requirements', v)}
                      disabled={!editable}
                      maxLength={10000}
                      optional
                    />
                  </Form.Item>
                )}
              </Form.Item>
            )}
          </Can>
          <Can I={ACTIONS.UPDATE} a={UI.OPENING_RESPONSIBILITIES} passThrough>
            {editable => (
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.position_advantages_initial !== curr.position_advantages_initial}
              >
                {({ getFieldValue, setFieldValue }) => (
                  <Form.Item className="rich-area-item" name="position_advantages">
                    <RichArea
                      initialValue={getFieldValue('position_advantages_initial')}
                      label={t('translation:create_opening.label_position_advantages')}
                      placeholder={t('translation:create_opening.ph_position_advantages')}
                      description={t('translation:create_opening.desc_position_advantages')}
                      onChange={v => setFieldValue('position_advantages', v)}
                      disabled={!editable}
                      maxLength={10000}
                      optional
                    />
                  </Form.Item>
                )}
              </Form.Item>
            )}
          </Can>
          <Can I={ACTIONS.UPDATE} a={UI.OPENING_COMMENT} passThrough>
            {editable => (
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.comment_initial !== curr.comment_initial}>
                {({ getFieldValue, setFieldValue }) => (
                  <Form.Item noStyle name="comment">
                    <RichArea
                      initialValue={getFieldValue('comment_initial')}
                      label={t('translation:create_opening.label_comment')}
                      onChange={v => setFieldValue('comment', v)}
                      placeholder={t('translation:create_opening.ph_comment')}
                      disabled={!editable}
                      maxLength={10000}
                      optional
                    />
                  </Form.Item>
                )}
              </Form.Item>
            )}
          </Can>
        </Card>
      </Form>
      <Popup
        open={openConfirmation}
        title={t('translation:create_opening.title_discard_changes')}
        cancelButtonProps={{
          onClick: onCloseExitConfirmation,
          children: t('translation:create_opening.button_back_to_page'),
        }}
        okButtonProps={{ onClick: toOpeningsList, children: t('translation:create_opening.button_discard') }}
      >
        {t('translation:create_opening.text_changes_not_saved')}
      </Popup>
    </div>
  );
};
